import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { trackLead } from "@/lib/pixel";

/**
 * Shared "Me interesa" dialog. Same component for all catalog categories.
 * Snapshots unit_id + unit title into `interes` (pre-filled, editable).
 */
export function InterestDialog({ unit, open, onClose }) {
  const [form, setForm] = useState({
    nombre: "",
    telefono: "",
    correo: "",
    interes: unit ? `Me interesa la unidad "${unit.titulo}".` : "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function onSubmit(e) {
    e.preventDefault();
    if (!unit) return;
    setSubmitting(true);
    try {
      await api.post("/catalog/interests", {
        unit_id: unit.id,
        nombre: form.nombre.trim(),
        telefono: form.telefono.trim(),
        correo: form.correo.trim(),
        interes: form.interes.trim(),
      });
      setDone(true);
      trackLead({
        content_name: unit?.titulo || "Interés en unidad",
        content_category: unit?.category || "catalog",
      });
      toast.success("Recibimos tu interés. Te contactamos pronto.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  function handleOpenChange(o) {
    if (!o) {
      setDone(false);
      onClose();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg" data-testid="interest-dialog">
        {done ? (
          <div className="py-6 text-center">
            <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Interés registrado</p>
            <h3 className="mt-3 font-serif text-2xl text-[#3E5641]">Gracias.</h3>
            <p className="mt-3 text-sm text-[#6B7678]">Un asesor te contactará por teléfono o WhatsApp.</p>
            <button onClick={() => handleOpenChange(false)} className="btn-primary mt-6" data-testid="interest-close">
              Cerrar
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit}>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Me interesa esta unidad</DialogTitle>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <div>
                <Label className="field-label">Nombre completo *</Label>
                <Input required value={form.nombre} onChange={(e) => setF("nombre", e.target.value)} data-testid="interest-nombre" />
              </div>
              <div>
                <Label className="field-label">Teléfono *</Label>
                <Input required value={form.telefono} onChange={(e) => setF("telefono", e.target.value)} data-testid="interest-tel" />
              </div>
              <div>
                <Label className="field-label">Correo *</Label>
                <Input type="email" required value={form.correo} onChange={(e) => setF("correo", e.target.value)} data-testid="interest-correo" />
              </div>
              <div>
                <Label className="field-label">Interés *</Label>
                <Textarea
                  required
                  rows={3}
                  value={form.interes}
                  onChange={(e) => setF("interes", e.target.value)}
                  data-testid="interest-interes"
                />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <button type="button" onClick={() => handleOpenChange(false)} className="btn-outline-dark">
                Cancelar
              </button>
              <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-60" data-testid="interest-submit">
                {submitting ? "Enviando..." : "Enviar interés"}
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
